<template>
  <div :class="$vuetify.breakpoint.mdAndDown ? 'mobile' : ''">
    <v-row no-gutters justify="space-between">
      <v-col no-gutters cols=10>
        <label style="float:left" class="input-field-label ma-0 text-capitalize d-block">
          {{ $t(`field.captcha`) }}<span v-if="!isOptional" class="red--text ml-1">*</span></label>
      </v-col>
      <v-col no-gutters cols=2></v-col>
      <v-col no-gutters cols=8 md="7" lg="8">
        <v-text-field
            class="input-field elevation-0 hide-details"
            :value="value"
            outlined
            type="text"
            :rules="rules"
            :error-messages="errorMsg"
            @input="input"
            @keydown.space="preventLeadingSpace"
            @keydown.native.space.prevent
            autocomplete="new-password"
            :autofocus="true">
          <template v-slot:append-outer>
            <slot name="form-field-append-outer"></slot>
          </template>
          <template v-slot:append>
            <slot name="form-field-append"></slot>
          </template>
        </v-text-field>
      </v-col>
      <v-col no-gutters cols=4 @click="refreshCaptchaCode" class="captcha-code">
        <img v-if="serverIssued && captchaImage" class="captcha captcha-image" :src="captchaImage" alt="captcha"/>
        <div v-else-if="serverIssued" class="captcha captcha-image captcha-placeholder">
          <v-icon small>mdi-reload</v-icon>
        </div>
        <captcha-code v-else class="captcha" ref="code" :enable-click="true" text-fill-color="#ffffff" :width="68"></captcha-code>
        <a class="refresh"><v-icon>mdi-refresh</v-icon></a>
      </v-col>
    </v-row>
  </div>
</template>

<script >
import {MEMBER_ISSUE_CAPTCHA, MEMBER_REQUEST_CAPTCHA_CODE} from "@/store/member.module";

/*
 * Two modes, and the default is deliberately the old one.
 *
 * LEGACY (serverIssued = false - every form except forgot password). This component
 * picks the number, draws it on a canvas, and posts the answer to the server, which
 * stores it and later compares the caller's answer against the caller's own answer.
 * It challenges nobody - the 2026-09-21..23 flood walked past it at ~170 SMS a
 * minute - but it is what every form on the site currently expects, so it stays
 * exactly as it was until each one is migrated deliberately.
 *
 * SERVER ISSUED (serverIssued = true - forgot password only for now). The server
 * picks the answer, draws it, and returns only an id and a picture. Two things
 * follow: the answer is not available on the client at all, and an issued captcha
 * is spent by one attempt right or wrong, so a fresh one is fetched after every
 * failure (see the errorMsg watcher).
 *
 * Read the id with getCaptchaId(), which is correct in both modes. Do not reach for
 * $refs.code.id from a parent - that child only exists in legacy mode.
 */
export default {
  name: 'Captcha',
  props: {
    value: {
      required: true,
      default: ''
    },
    label: {
      type: String,
      required: false,
      default: ''
    },
    rules: {
      type: Array,
      required: false,
      rules: () => []
    },
    isOptional: {
      type: Boolean,
      required: false,
      default: false
    },
    errorMsg: {
      type: String,
      required: false
    },
    /* Opt in per form. Off by default so migrating one flow cannot change any
       other, which is the whole reason this is a prop and not a global switch. */
    serverIssued: {
      type: Boolean,
      required: false,
      default: false
    },
  },
  data() {
    return {
      captchaId: '',
      captchaImage: '',
      refreshTimer: null
    }
  },
  watch: {
    /* Server-issued only: the server spends the captcha on the first attempt, so
       once the parent reports a failure the picture on screen is already dead and
       the player would be told they are wrong no matter what they type. Legacy
       captchas are reusable, so this must not fire for them - refreshing there
       would change behaviour on forms that are not being migrated. */
    errorMsg(value) {
      if (value && this.serverIssued) {
        this.refreshCaptchaCode();
      }
    }
  },
  mounted() {
    this.$nextTick(() => {
      this.requestCaptchaCode();

      /* Legacy only. That widget re-registers its answer with the server every two
         minutes because the server side of it expires in two minutes, so the timer
         is load bearing there.
         *
         * A server-issued captcha must NOT be on a timer. Three reasons, and the
         * first one took the form down on staging:
         *   - /member/captcha sits behind sms.ip.throttle at 20 requests per 30
         *     minutes. A 120s timer spends 15 of those just sitting on the page,
         *     so a few dialog opens blocks the player's own address for half an
         *     hour and every refresh then 429s.
         *   - each refresh replaces the picture the player may be part way through
         *     reading, and clears what they typed against it.
         *   - it is not needed. The captcha outlives the form's useful life, and
         *     if it does expire the errorMsg watcher hands them a fresh one on the
         *     failed submit.
         *
         * Held in a field so beforeDestroy can actually clear it. The previous code
         * passed the method itself to clearInterval instead of the timer id, so the
         * interval outlived the component and polled for the life of the page. */
      if (!this.serverIssued) {
        this.refreshTimer = setInterval(this.refreshCaptchaCode, 120000);
      }
    })
  },
  methods: {
    refreshCaptchaCode() {
      if (!this.serverIssued && this.$refs.code) {
        this.$refs.code.changeCode();
      }
      this.requestCaptchaCode();
    },
    requestCaptchaCode () {
      return this.serverIssued ? this.requestServerCaptcha() : this.requestLegacyCaptchaCode();
    },
    requestServerCaptcha () {
      return this.$store.dispatch(`${MEMBER_ISSUE_CAPTCHA}`).then(response => {
        const payload = (response && response.data) ? response.data : null;
        if (payload && payload.captcha_id) {
          this.captchaId = payload.captcha_id;
          this.captchaImage = payload.image;
          /* The old picture is gone, so anything typed against it is stale. */
          this.$emit('input', '');
        }
      }).catch(() => {
        /* Keep whatever is on screen. A failed refresh must not blank the field out
           from under someone part way through typing, and if there was never a
           picture the template shows the reload placeholder rather than a broken
           image - which is what an empty src renders as, and what the throttle
           produced on staging. Clicking the box retries. */
      });
    },
    requestLegacyCaptchaCode () {
      let data = {
        captcha_id: this.$refs.code.id,
        captcha_code: this.$refs.code.getCode()
      };
      this.$store.dispatch(`${MEMBER_REQUEST_CAPTCHA_CODE}`, {
        data
      });
    },
    /* The id to quote back as captcha_id, correct in whichever mode this instance
       is in. Parents should use this instead of reaching into $refs.code. */
    getCaptchaId () {
      return this.serverIssued ? this.captchaId : this.$refs.code.id;
    },
    getCode () {
      return this.serverIssued ? '' : this.$refs.code.getCode();
    },
    preventLeadingSpace(e) {
      // only prevent the keypress if the value is blank
      if (!e.target.value) e.preventDefault()
      // otherwise, if the leading character is a space, remove all leading white-space
      else if (e.target.value[0] == ' ') e.target.value = e.target.value.replace(/^\s*/, '')
    },
    input(value) {
      this.$emit('input', value);
    },
  },
  beforeDestroy() {
    clearInterval(this.refreshTimer);
  },
}
</script>

<style lang="scss">
.v-text-field--outlined {
  border-radius: 10px;

  fieldset {
    border-width: 1px;
    border-color: #3A3A3A;
  }
}

.v-text-field--outlined.v-input--is-verify fieldset {
  border-color: green;
  border-width: 2px;
}

.v-input .v-label {
  line-height: 14px !important;
}

.input-field-label {
  font-size: 16px;
}

.input-field {
  .v-select.v-text-field--outlined:not(.v-text-field--single-line) .v-select__selections {
    padding: 0px !important;
  }

  .v-input__append-outer {
    margin-top: 0px !important;
  }

  &.v-input--is-readonly {
    input {
      color: #3A3A3A !important;
      opacity: 0.7;
    }

    .v-input__slot {
      background-color: rgba(0, 0, 0, 0.1);
    }


  }

  .v-input__control {
    margin-bottom: 2px;

    .v-input__slot {
      // border-radius: 25px;
      min-height: 40px;
      height: 40px;
      margin-bottom: 2px;

      .v-text-field__slot {
        .v-label {
          line-height: 14px !important;
          top: 15px;
        }
      }

      .v-input__append-inner {
        margin-top: 14px;
      }

      .v-select__slot {
        .v-input__append-inner {
          margin-top: 8px;
        }
      }
    }

    .v-text-field__details {
      //padding: 0px 4px;
      .v-messages__message {
        font-size: 11px;
      }
    }
  }

}

@media (max-width: 959px) {
  .v-input {
    font-size: 14px;
  }
  .input-field {
    .v-input__control {
      margin-bottom: 2px;

      .v-input__slot {
        // border-radius: 25px;
        min-height: 35px !important;
        height: 35px !important;
        margin-bottom: 2px;

        .v-text-field__slot {
          .v-label {
            line-height: 14px !important;
            top: 12px !important;
          }
        }

        .v-input__append-inner {
          margin-top: 6px !important;
        }
      }

      .v-text-field__details {
        //padding: 0px 4px;
        margin-bottom: 5px !important;

        .v-messages__message {
          font-size: 10px;
        }
      }
    }
  }
  .input-field-label {
    font-size: 14px;
  }
  .input-field {
    .v-label {
      font-size: 14px !important;
    }
  }


}

@media (max-width: 425px) {
  .input-field {
    .v-input__control {
      margin-bottom: 2px;

      .v-input__slot {
        // border-radius: 25px;
        min-height: 35px !important;
        height: 35px !important;
        margin-bottom: 2px;

        .v-text-field__slot {
          .v-label {
            line-height: 14px !important;
            top: 12px !important;
          }
        }

        .v-input__append-inner {
          margin-top: 6px !important;
        }
      }

      .v-text-field__details {
        //padding: 0px 4px;
        margin-bottom: 5px !important;

        .v-messages__message {
          font-size: 10px;
        }
      }
    }
  }
}

.captcha-code {
  display: flex;
  justify-content: flex-end;
  .captcha {
    min-height: 40px;
    height: 40px;
    text-align: center;
    background-color: var(--v-primary-base);
  }

  /* Height comes from .captcha above (40px), which is also .refresh's height, so
     the picture and the reload button line up. Do NOT set a height here - an
     earlier version used height:100%, which overrode that 40px and, with no
     resolvable height on the flex parent, grew the image past the button.
     Only the width and the fit belong to this rule.

     The server renders at the same 1.7:1 this 68x40 strip is, so filling it
     neither letterboxes nor distorts. object-fit: fill rather than contain on
     purpose: if the two ever drift apart, a slightly stretched digit is a better
     failure than black bands above and below a shrunken one. Keep
     config/captcha.php width/height in proportion with this. */
  .captcha-image {
    width: 68px;
    object-fit: fill;
    cursor: pointer;
  }

  /* Shown until the first picture arrives, and if a fetch fails. Same footprint as
     the image so the row does not jump, and it reads as "tap to retry" rather than
     as a broken page. */
  .captcha-placeholder {
    display: flex;
    align-items: center;
    justify-content: center;
    opacity: 0.6;
  }

  .refresh {
    display: inline-flex;
    min-height: 40px;
    height: 40px;
    width: 28px;
    background-color: var(--v-primary-base);

    i {
      color: white;
    }
  }
}
</style>
